/**
 * ZoomResetButton - כפתור איפוס זום למובייל
 * 
 * גרסה משודרגת עם:
 * 1. ביצועים משופרים - עדכון DOM ישיר במקום useState לפוזיציה
 * 2. איפוס זום רך (Soft Reset) ללא ריענון דף
 * 3. טיפול בזום קיצוני (>300%)
 * 4. עיצוב Glassmorphism מודרני
 */

import { useState, useEffect, useRef, useCallback } from "react";
import { Minimize2 } from "lucide-react";

export const ZoomResetButton = () => {
  // === STATE ===
  // רק state הכרחי - לא position כי זה יגרום לרינדורים מיותרים
  const [showButton, setShowButton] = useState(false);
  const [isResetting, setIsResetting] = useState(false);
  const [zoomTier, setZoomTier] = useState('normal'); // 'normal' | 'high' | 'extreme'
  
  // === REFS ===
  // שימוש ב-refs לביצועים - עדכון DOM ישיר ללא re-render
  const buttonRef = useRef(null);
  const overlayRef = useRef(null);
  const timeoutRef = useRef(null);
  const rafRef = useRef(null); // requestAnimationFrame reference
  const lastZoomState = useRef(false);
  const initialWidthRef = useRef(null);
  const currentScale = useRef(1);
  
  // זיהוי מובייל
  const isMobile = typeof navigator !== 'undefined' && 
    /iPhone|iPad|iPod|Android/i.test(navigator.userAgent);

  /**
   * עדכון מיקום הכפתור ישירות ב-DOM
   * שימוש ב-requestAnimationFrame לביצועים אופטימליים
   * ללא useState = ללא re-renders מיותרים
   */
  const updateButtonPosition = useCallback(() => {
    if (!buttonRef.current || !window.visualViewport) return;
    
    const vv = window.visualViewport;
    const top = vv.offsetTop + 12;
    const left = vv.offsetLeft + 10;
    
    // עדכון ישיר ב-DOM - הרבה יותר מהיר מ-setState
    buttonRef.current.style.top = `${top}px`;
    buttonRef.current.style.left = `${left}px`;
    
    // עדכון גם ל-overlay אם קיים
    if (overlayRef.current) {
      overlayRef.current.style.top = `${vv.offsetTop}px`;
      overlayRef.current.style.left = `${vv.offsetLeft}px`;
      overlayRef.current.style.width = `${vv.width}px`;
      overlayRef.current.style.height = `${vv.height}px`;
    }
  }, []);

  /**
   * קביעת דרגת הזום לעיצוב הכפתור
   * normal: עד 150% - כפתור מלא
   * high: 150%-300% - כפתור מוקטן
   * extreme: מעל 300% - אייקון בלבד
   */
  const getZoomTier = useCallback((scale) => {
    if (scale > 3) return 'extreme';      // מעל 300%
    if (scale > 1.5) return 'high';       // 150%-300%
    return 'normal';                       // עד 150%
  }, []);

  /**
   * בדיקת מצב זום עם requestAnimationFrame
   * לביצועים אופטימליים
   */
  const checkZoom = useCallback(() => {
    if (!window.visualViewport) return;
    
    const scale = window.visualViewport.scale;
    currentScale.current = scale;
    const isZoomed = scale > 1.05;
    
    // עדכון דרגת זום
    const newTier = getZoomTier(scale);
    setZoomTier(newTier);
    
    if (isZoomed) {
      // שימוש ב-RAF לעדכון חלק
      if (rafRef.current) cancelAnimationFrame(rafRef.current);
      rafRef.current = requestAnimationFrame(updateButtonPosition);
    }

    // פעולה רק בשינוי מצב
    if (isZoomed !== lastZoomState.current) {
      lastZoomState.current = isZoomed;
      
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
        timeoutRef.current = null;
      }
      
      if (isZoomed) {
        // הצג כפתור אחרי 3 שניות
        timeoutRef.current = setTimeout(() => {
          setShowButton(true);
          requestAnimationFrame(updateButtonPosition);
        }, 3000);
      } else {
        setShowButton(false);
      }
    }
  }, [updateButtonPosition, getZoomTier]);

  /**
   * === SOFT RESET - איפוס זום ללא ריענון ===
   * 
   * הטכניקה:
   * 1. שינוי viewport meta ל-maximum-scale=1.0, user-scalable=no
   * 2. המתנה לאכיפת הדפדפן
   * 3. החזרת viewport למצב המקורי
   * 4. Fallback לריענון אם נכשל
   */
  const softResetZoom = useCallback(() => {
    return new Promise((resolve) => {
      const viewportMeta = document.querySelector('meta[name="viewport"]');
      if (!viewportMeta) {
        resolve(false);
        return;
      }

      // שמירת התוכן המקורי
      const originalContent = viewportMeta.getAttribute('content');
      
      // שלב 1: הגבלת זום מקסימלי
      viewportMeta.setAttribute('content', 
        'width=device-width, initial-scale=1.0, minimum-scale=1.0, maximum-scale=1.0, user-scalable=no'
      );

      // שלב 2: המתנה לאכיפת הדפדפן
      setTimeout(() => {
        // שלב 3: בדיקה אם הזום התאפס
        const newScale = window.visualViewport?.scale || 1;
        
        if (newScale <= 1.1) {
          // הצלחה! החזר viewport למצב רגיל אחרי עוד רגע
          setTimeout(() => {
            viewportMeta.setAttribute('content', 
              originalContent || 'width=device-width, initial-scale=1.0, minimum-scale=1.0, maximum-scale=5.0, user-scalable=yes'
            );
          }, 200);
          resolve(true);
        } else {
          // נכשל - החזר למצב מקורי
          viewportMeta.setAttribute('content', originalContent);
          resolve(false);
        }
      }, 150);
    });
  }, []);

  /**
   * === HARD RESET - ריענון דף כ-Fallback ===
   * שומר state ב-sessionStorage
   */
  const hardResetZoom = useCallback(() => {
    // שמירת מיקום גלילה
    const scrollY = window.pageYOffset || document.documentElement.scrollTop;
    sessionStorage.setItem('zoom_reset_scroll', scrollY.toString());
    
    // שמירת נתוני טפסים פתוחים (אם יש)
    const forms = document.querySelectorAll('form');
    const formData = {};
    forms.forEach((form, index) => {
      const data = new FormData(form);
      formData[index] = Object.fromEntries(data.entries());
    });
    if (Object.keys(formData).length > 0) {
      sessionStorage.setItem('zoom_reset_forms', JSON.stringify(formData));
    }
    
    // שינוי viewport וניווט
    const viewportMeta = document.querySelector('meta[name="viewport"]');
    if (viewportMeta) {
      viewportMeta.setAttribute('content', 
        'width=device-width, initial-scale=1.0, minimum-scale=1.0, maximum-scale=1.0, user-scalable=no'
      );
    }

    const currentUrl = new URL(window.location.href);
    currentUrl.searchParams.set('_zr', Date.now().toString());
    
    setTimeout(() => {
      window.location.replace(currentUrl.toString());
    }, 100);
  }, []);

  /**
   * פונקציית איפוס ראשית - מנסה soft reset, אחרת hard reset
   */
  const resetZoom = useCallback(async () => {
    setIsResetting(true);
    
    // ניסיון ראשון: Soft Reset
    const softResetSuccess = await softResetZoom();
    
    if (softResetSuccess) {
      // הצלחה! סגור את הכפתור בחינניות
      lastZoomState.current = false;
      setTimeout(() => {
        setShowButton(false);
        setIsResetting(false);
      }, 300);
    } else {
      // Fallback: Hard Reset
      hardResetZoom();
    }
  }, [softResetZoom, hardResetZoom]);

  // === EFFECTS ===
  
  // שחזור state אחרי ריענון
  useEffect(() => {
    const url = new URL(window.location.href);
    if (url.searchParams.has('_zr')) {
      // ניקוי URL
      url.searchParams.delete('_zr');
      window.history.replaceState({}, '', url.pathname + url.search + url.hash);
      
      // שחזור גלילה
      const savedScroll = sessionStorage.getItem('zoom_reset_scroll');
      if (savedScroll) {
        setTimeout(() => {
          window.scrollTo({ top: parseInt(savedScroll, 10), behavior: 'instant' });
          sessionStorage.removeItem('zoom_reset_scroll');
        }, 150);
      }
      
      // שחזור נתוני טפסים (אם יש)
      const savedForms = sessionStorage.getItem('zoom_reset_forms');
      if (savedForms) {
        try {
          const formData = JSON.parse(savedForms);
          const forms = document.querySelectorAll('form');
          forms.forEach((form, index) => {
            if (formData[index]) {
              Object.entries(formData[index]).forEach(([name, value]) => {
                const input = form.querySelector(`[name="${name}"]`);
                if (input) input.value = value;
              });
            }
          });
        } catch (e) { /* ignore */ }
        sessionStorage.removeItem('zoom_reset_forms');
      }
    }
  }, []);

  // Event listeners לזיהוי זום
  useEffect(() => {
    if (!isMobile) return;

    if (window.visualViewport && !initialWidthRef.current) {
      initialWidthRef.current = window.visualViewport.width;
    }

    const viewport = window.visualViewport;
    
    const handleViewportChange = () => {
      // שימוש ב-RAF למניעת throttling
      if (rafRef.current) cancelAnimationFrame(rafRef.current);
      rafRef.current = requestAnimationFrame(() => {
        checkZoom();
        if (showButton && buttonRef.current) {
          updateButtonPosition();
        }
      });
    };

    if (viewport) {
      viewport.addEventListener('resize', handleViewportChange);
      viewport.addEventListener('scroll', handleViewportChange);
    }
    
    // מעקב מחוות pinch
    let lastTouchCount = 0;
    const handleTouchStart = (e) => { lastTouchCount = e.touches.length; };
    const handleTouchEnd = () => {
      if (lastTouchCount >= 2) setTimeout(checkZoom, 200);
      lastTouchCount = 0;
    };
    const handleTouchMove = () => {
      if (showButton) {
        if (rafRef.current) cancelAnimationFrame(rafRef.current);
        rafRef.current = requestAnimationFrame(updateButtonPosition);
      }
    };
    
    document.addEventListener('touchstart', handleTouchStart, { passive: true });
    document.addEventListener('touchend', handleTouchEnd, { passive: true });
    document.addEventListener('touchmove', handleTouchMove, { passive: true });
    document.addEventListener('gestureend', () => setTimeout(checkZoom, 200));

    return () => {
      if (viewport) {
        viewport.removeEventListener('resize', handleViewportChange);
        viewport.removeEventListener('scroll', handleViewportChange);
      }
      document.removeEventListener('touchstart', handleTouchStart);
      document.removeEventListener('touchend', handleTouchEnd);
      document.removeEventListener('touchmove', handleTouchMove);
      if (timeoutRef.current) clearTimeout(timeoutRef.current);
      if (rafRef.current) cancelAnimationFrame(rafRef.current);
    };
  }, [isMobile, checkZoom, showButton, updateButtonPosition]);

  // לא מציג בדסקטופ או כשאין צורך
  if (!isMobile || !showButton) return null;

  // === RENDER ===
  
  // סגנונות לפי דרגת זום
  const tierStyles = {
    // עד 150% - כפתור מלא
    normal: {
      padding: 'pl-2.5 pr-3.5 py-2.5',
      iconSize: 'w-7 h-7',
      iconInner: 'h-4 w-4',
      fontSize: 'text-sm',
      showText: true,
      showEmoji: true,
      opacity: 1,
      scale: 1,
    },
    // 150%-300% - כפתור מוקטן
    high: {
      padding: 'pl-2 pr-2.5 py-2',
      iconSize: 'w-6 h-6',
      iconInner: 'h-3.5 w-3.5',
      fontSize: 'text-xs',
      showText: true,
      showEmoji: false,
      opacity: 0.95,
      scale: Math.max(0.7, 1 / (currentScale.current * 0.6)),
    },
    // מעל 300% - אייקון בלבד (מינימליסטי)
    extreme: {
      padding: 'p-2.5',
      iconSize: 'w-6 h-6',
      iconInner: 'h-4 w-4',
      fontSize: 'text-xs',
      showText: false,
      showEmoji: false,
      opacity: 0.8,
      scale: Math.max(0.5, 1 / (currentScale.current * 0.5)),
    },
  };
  
  const style = tierStyles[zoomTier];

  return (
    <>
      {/* === מסך מעבר בזמן איפוס === */}
      {isResetting && (
        <div 
          ref={overlayRef}
          className="fixed z-[999998] flex items-center justify-center"
          style={{
            top: window.visualViewport?.offsetTop || 0,
            left: window.visualViewport?.offsetLeft || 0,
            width: window.visualViewport?.width || '100%',
            height: window.visualViewport?.height || '100%',
            background: 'rgba(250, 248, 245, 0.95)',
            backdropFilter: 'blur(20px)',
            WebkitBackdropFilter: 'blur(20px)',
            animation: 'fadeIn 0.2s ease-out',
          }}
        >
          <div 
            className="flex flex-col items-center gap-3"
            style={{ animation: 'scaleIn 0.3s ease-out' }}
          >
            <div 
              className="w-14 h-14 rounded-2xl flex items-center justify-center"
              style={{
                background: 'linear-gradient(135deg, #4A5D43 0%, #5D7356 100%)',
                boxShadow: '0 8px 32px rgba(74, 93, 67, 0.3)',
              }}
            >
              <Minimize2 
                className="h-7 w-7 text-white" 
                style={{ animation: 'spin 1s linear infinite' }}
              />
            </div>
            <span className="text-sm text-[#4A5D43] font-medium">מאפס תצוגה...</span>
          </div>
        </div>
      )}
      
      {/* === כפתור איפוס - עיצוב Glassmorphism === */}
      <button
        ref={buttonRef}
        onClick={resetZoom}
        disabled={isResetting}
        className={`fixed z-[999999] group flex items-center gap-2 ${style.padding}
          rounded-2xl
          font-medium text-white
          transition-all duration-200 ease-out
          active:scale-95 disabled:opacity-50
          focus:outline-none focus:ring-2 focus:ring-white/50`}
        style={{
          // Glassmorphism
          background: 'linear-gradient(135deg, rgba(74, 93, 67, 0.9) 0%, rgba(93, 115, 86, 0.85) 100%)',
          backdropFilter: 'blur(16px) saturate(180%)',
          WebkitBackdropFilter: 'blur(16px) saturate(180%)',
          border: '1px solid rgba(255, 255, 255, 0.2)',
          boxShadow: `
            0 8px 32px rgba(74, 93, 67, 0.35),
            0 4px 12px rgba(0, 0, 0, 0.1),
            inset 0 1px 0 rgba(255, 255, 255, 0.15)
          `,
          // מיקום ראשוני - יעודכן ב-RAF
          top: '12px',
          left: '10px',
          // סקייל לפי דרגת זום
          transform: `scale(${style.scale})`,
          transformOrigin: 'top left',
          opacity: style.opacity,
          // אנימציית כניסה מוטמעת
          animation: 'slideInLeft 0.4s cubic-bezier(0.16, 1, 0.3, 1)',
        }}
        aria-label="איפוס תצוגה"
      >
        {/* אייקון עם אפקט זכוכית */}
        <span 
          className={`flex items-center justify-center ${style.iconSize} rounded-xl
            transition-all duration-200 group-active:scale-95`}
          style={{
            background: 'rgba(255, 255, 255, 0.2)',
            backdropFilter: 'blur(8px)',
            boxShadow: 'inset 0 1px 0 rgba(255, 255, 255, 0.2)',
          }}
        >
          <Minimize2 className={`${style.iconInner} text-white drop-shadow-sm`} />
        </span>
        
        {/* טקסט - מוסתר בזום קיצוני */}
        {style.showText && (
          <span className={`${style.fontSize} font-semibold tracking-wide drop-shadow-sm`}>
            {zoomTier === 'high' ? 'איפוס' : 'איפוס תצוגה'}
          </span>
        )}
        
        {/* אימוג'י - רק בזום רגיל */}
        {style.showEmoji && (
          <span className="text-sm opacity-90">🌿</span>
        )}
        
        {/* אפקט shine */}
        <span 
          className="absolute inset-0 rounded-2xl overflow-hidden pointer-events-none"
          style={{
            background: 'linear-gradient(105deg, transparent 40%, rgba(255,255,255,0.12) 45%, rgba(255,255,255,0.12) 55%, transparent 60%)',
          }}
        />
      </button>

      {/* === אנימציות CSS מוטמעות === */}
      <style>{`
        @keyframes slideInLeft {
          from {
            opacity: 0;
            transform: translateX(-24px) scale(0.85);
          }
          to {
            opacity: 1;
            transform: translateX(0) scale(1);
          }
        }
        
        @keyframes fadeIn {
          from { opacity: 0; }
          to { opacity: 1; }
        }
        
        @keyframes scaleIn {
          from {
            opacity: 0;
            transform: scale(0.9);
          }
          to {
            opacity: 1;
            transform: scale(1);
          }
        }
        
        @keyframes spin {
          from { transform: rotate(0deg); }
          to { transform: rotate(360deg); }
        }
      `}</style>
    </>
  );
};
